import { useEffect } from "react";

/**
 * The height a full-screen surface can actually paint into, published as
 * `--app-vh`, plus a `data-keyboard` flag on the root element.
 *
 * A page that fills the window has three different answers to "how tall is the
 * window?" depending on where it runs, and picking the wrong one is what makes
 * a chat screen feel broken:
 *
 * - `100vh` is the *large* viewport, so on mobile Safari it is taller than what
 *   you can see whenever the toolbars are up: the message input sits below the
 *   fold.
 * - `100dvh` tracks the toolbars but does nothing about the software keyboard
 *   on iOS, where the keyboard covers the page instead of resizing it. The
 *   browser's answer is to scroll the layout viewport to reveal the focused
 *   field, which a page with no scroll of its own cannot do: the input ends up
 *   behind the keyboard.
 * - Inside the native WebView neither applies, because the shell resizes the
 *   web view itself and injects its own safe-area insets.
 *
 * `visualViewport.height` is the one measurement that is right in all three:
 * it is what the user can currently see, keyboard and browser chrome already
 * subtracted. Sizing the surface to it means the layout ends exactly where the
 * keyboard begins, so nothing has to scroll to stay visible.
 *
 * `data-keyboard="open"` exists because the bottom safe-area inset does not
 * retract on its own in mobile Safari: with a keyboard covering the home
 * indicator, padding for it is a strip of dead space above the keys. See
 * `.bottom-safe-padding-adaptive`, and `apply` below for why the keyboard has
 * to be detected two different ways.
 */
const HEIGHT_VAR = "--app-vh";
// How far the visible band has been panned down inside the layout viewport.
const OFFSET_VAR = "--app-vv-top";
const KEYBOARD_ATTR = "data-keyboard";
// Sizes the document itself to `--app-vh` — see the rule in index.css.
const LOCK_CLASS = "app-viewport-locked";

// Below this, the viewport shrank for some reason other than a keyboard —
// a toolbar sliding in, a rotation mid-measure, an accessory bar on its own.
const KEYBOARD_MIN_INSET = 120;

function isEditing(): boolean {
  const el = document.activeElement;
  if (!(el instanceof HTMLElement)) return false;
  return el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable;
}

export function useAppViewportHeight(enabled: boolean) {
  useEffect(() => {
    if (!enabled || typeof window === "undefined") return;

    const root = document.documentElement;
    const viewport = window.visualViewport;

    // The height the window settles at with nothing focused — the baseline a
    // keyboard is measured against. Sampled continuously while the user is not
    // typing, so rotating or resizing re-establishes it.
    let restingHeight = 0;

    const apply = () => {
      const height = Math.round(viewport?.height ?? window.innerHeight);
      if (!height) return;
      root.style.setProperty(HEIGHT_VAR, `${height}px`);

      // Where the visible band currently sits inside the layout viewport.
      //
      // With a keyboard covering the page, the layout viewport keeps its full
      // height and iOS lets the user pan the visible band around inside it —
      // a browser-level gesture that exists to reach content behind the keys,
      // and one no amount of `overflow: hidden` can refuse. A surface fixed to
      // the layout viewport slides up out of view with that pan and leaves a
      // strip of nothing below it. Fixed to *this* instead, the surface rides
      // the band and the gesture has nothing left to reveal.
      root.style.setProperty(OFFSET_VAR, `${Math.round(viewport?.offsetTop ?? 0)}px`);

      const editing = isEditing();
      if (!editing) restingHeight = height;

      // A keyboard shows up in one of two shapes, and which one depends on the
      // browser rather than on anything we control:
      //
      // - It covers the page. The layout viewport keeps its full height and
      //   only the visible one shrinks, so the difference between them is the
      //   keyboard. This was iOS for years.
      // - It resizes the page, which is what `interactive-widget=resizes-content`
      //   in index.html asks for and what Android and current iOS Safari do.
      //   Then *both* heights shrink together, their difference stays 0, and
      //   the only evidence is that the window got shorter while a field was
      //   focused.
      //
      // Reading only the first is what left the composer sitting a home
      // indicator's worth above the keys on iOS Safari: the inset below it is
      // there to clear a home indicator the keyboard was already covering, and
      // nothing ever said the keyboard was up.
      const covered = viewport
        ? window.innerHeight - viewport.height - viewport.offsetTop
        : 0;
      const lostWhileTyping = editing ? restingHeight - height : 0;

      if (Math.max(covered, lostWhileTyping) >= KEYBOARD_MIN_INSET) {
        root.setAttribute(KEYBOARD_ATTR, "open");
      } else {
        root.removeAttribute(KEYBOARD_ATTR);
      }

      // Safari scrolls the page to reveal the focused field, and it decides how
      // far using the geometry from *before* the keyboard resized anything. By
      // the time the surface has shrunk to fit above the keys there is nothing
      // left to scroll and the field was never out of view — but the page can
      // be left holding that offset, which looks like the composer drifting up
      // off the top of the keyboard. Only ever corrected when the document has
      // no scroll of its own, so it can never fight a real one.
      if (
        window.scrollY !== 0 &&
        document.documentElement.scrollHeight <= window.innerHeight + 1
      ) {
        window.scrollTo(0, 0);
      }
    };

    // Focus moving between two fields blurs the first before it focuses the
    // second, so reading `activeElement` synchronously would see the gap in
    // between and flash the inset back for a frame.
    let queued = 0;
    const applyNextFrame = () => {
      if (queued) return;
      queued = requestAnimationFrame(() => {
        queued = 0;
        apply();
      });
    };

    apply();
    // After the first measurement, so the document is never briefly locked to
    // the `dvh` fallback.
    root.classList.add(LOCK_CLASS);

    viewport?.addEventListener("resize", apply);
    // The visual viewport scrolls independently of the document when the
    // keyboard pushes it around, and that moves `offsetTop` without firing a
    // resize.
    viewport?.addEventListener("scroll", apply);
    window.addEventListener("resize", apply);
    window.addEventListener("orientationchange", apply);
    document.addEventListener("focusin", applyNextFrame);
    document.addEventListener("focusout", applyNextFrame);

    return () => {
      if (queued) cancelAnimationFrame(queued);
      viewport?.removeEventListener("resize", apply);
      viewport?.removeEventListener("scroll", apply);
      window.removeEventListener("resize", apply);
      window.removeEventListener("orientationchange", apply);
      document.removeEventListener("focusin", applyNextFrame);
      document.removeEventListener("focusout", applyNextFrame);
      root.classList.remove(LOCK_CLASS);
      root.style.removeProperty(HEIGHT_VAR);
      root.style.removeProperty(OFFSET_VAR);
      root.removeAttribute(KEYBOARD_ATTR);
    };
  }, [enabled]);
}
